import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../utils/api';

// Payment Modal Component
const PaymentModal = ({ paymentModal, setPaymentModal, fetchUserData }) => {
  if (!paymentModal) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a2e] rounded-2xl border border-purple-500/30 max-w-md w-full p-6 shadow-2xl">
        <div className="text-center">
          {paymentModal.type === 'success' && (
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          )}
          {paymentModal.type === 'error' && (
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          )}
          {paymentModal.type === 'cancelled' && (
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          )}
          <h3 className={`text-xl font-bold mb-2 ${
            paymentModal.type === 'success' ? 'text-green-400' :
            paymentModal.type === 'error' ? 'text-red-400' : 'text-yellow-400'
          }`}>
            {paymentModal.title}
          </h3>
          <p className="text-gray-300 mb-6">{paymentModal.message}</p>
          <button
            onClick={() => {
              setPaymentModal(null);
              if (paymentModal.type === 'success') {
                fetchUserData();
              }
            }}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              paymentModal.type === 'success' 
                ? 'bg-green-600 hover:bg-green-500 text-white'
                : paymentModal.type === 'error'
                ? 'bg-red-600 hover:bg-red-500 text-white'
                : 'bg-yellow-600 hover:bg-yellow-500 text-white'
            }`}
          >
            {paymentModal.type === 'success' ? 'Great!' : 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TeamDashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(null);
  const [creditPackages, setCreditPackages] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [subscriptionFeatures, setSubscriptionFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [error, setError] = useState('');
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [paymentModal, setPaymentModal] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('team_token');
    return { Authorization: `Bearer ${token}` };
  };

  // Check if user still has access (subscription status)
  const checkAccessStatus = async () => {
    try {
      const token = localStorage.getItem('team_token');
      if (!token) return;
      
      const response = await axios.get(`${API}/user/access-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If access is restricted, show error and redirect
      if (!response.data.has_access) {
        setError('Access denied. Please contact your organisation administrator.');
        // Clear tokens and redirect after showing message
        setTimeout(() => {
          localStorage.removeItem('team_token');
          localStorage.removeItem('team_user');
          localStorage.removeItem('token');
          localStorage.removeItem('userType');
          navigate('/team/login');
        }, 3000);
      }
    } catch (err) {
      // If 401/403, token is invalid or access denied
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.error('Access check failed:', err.response?.data?.detail);
        localStorage.removeItem('team_token');
        localStorage.removeItem('team_user');
        localStorage.removeItem('token');
        localStorage.removeItem('userType');
        navigate('/team/login');
      }
      // For other errors, silently continue (network issues etc)
    }
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();

      // Fetch all data in parallel (same pattern as admin portal)
      const [userRes, balanceRes, packagesRes, transactionsRes, featuresRes] = await Promise.all([
        axios.get(`${API}/me`, { headers }),
        axios.get(`${API}/neo/credits/balance`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/neo/credits/packages`, { headers }).catch(() => ({ data: { packages: [] } })),
        axios.get(`${API}/neo/credits/transactions?limit=20`, { headers }).catch(() => ({ data: { transactions: [] } })),
        axios.get(`${API}/user/subscription-features`, { headers }).catch(() => ({ data: { subscription_features: {} } }))
      ]);

      setUser(userRes.data);
      setBalance(balanceRes.data);
      setCreditPackages(packagesRes.data.packages || []);
      setTransactions(transactionsRes.data.transactions || []);
      setSubscriptionFeatures(featuresRes.data.subscription_features || {});
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('team_token');
        localStorage.removeItem('team_user');
        navigate('/team/login');
      } else {
        setError('Failed to load your data. Please try again.');
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('team_token');
    if (!token) {
      navigate('/team/login');
      return;
    }
    
    // Check subscription/access status on page load
    checkAccessStatus();
    fetchUserData();
    
    // Check for payment result from backend redirect
    // Backend redirects with ?payment=success|error|cancelled&credits=X&balance=Y
    let paymentStatus = null;
    let credits = null;
    let message = null;
    
    // Check URL search params (BrowserRouter)
    const searchParams = new URLSearchParams(window.location.search);
    paymentStatus = searchParams.get('payment');
    credits = searchParams.get('credits');
    message = searchParams.get('message');
    
    // Fallback: check hash for backward compatibility
    if (!paymentStatus && window.location.hash.includes('?')) {
      const hashParts = window.location.hash.split('?');
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        paymentStatus = hashParams.get('payment');
        credits = hashParams.get('credits');
        message = hashParams.get('message');
      }
    }
    
    if (paymentStatus === 'success') {
      setPaymentModal({
        type: 'success',
        title: 'Payment Successful!',
        message: `${credits || ''} credits have been added to your account.`
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'error') {
      setPaymentModal({
        type: 'error',
        title: 'Payment Failed',
        message: `${message || 'An error occurred during payment'}. Please contact support if you were charged.`
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      setPaymentModal({
        type: 'cancelled',
        title: 'Payment Cancelled',
        message: 'Your payment was cancelled. No charges were made.'
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [navigate]);

  const handleLogout = async () => {
    // Notify backend so this device's session is invalidated server-side.
    // Team members are single-device-enforced so there's only ever one
    // session to kill, but we still pass device_id to satisfy the 400 guard.
    try {
      const teamToken = localStorage.getItem('team_token');
      const deviceId = localStorage.getItem('deviceId') || localStorage.getItem('team_device_id') || 'web-team-dashboard';
      if (teamToken) {
        await axios.post(
          `${API}/logout`,
          { device_id: deviceId },
          { headers: { Authorization: `Bearer ${teamToken}` } }
        );
      }
    } catch (e) {
      console.warn('Backend team logout call failed (continuing with local clear):', e);
    }
    localStorage.removeItem('team_token');
    localStorage.removeItem('team_user');
    navigate('/team/login');
  };

  const handlePurchase = async (packageId) => {
    try {
      setPurchasing(true);
      const response = await axios.post(
        `${API}/neo/credits/purchase`,
        { package_id: packageId },
        { headers: getAuthHeaders() }
      );

      if (response.data.success && response.data.approval_url) {
        window.location.href = response.data.approval_url;
      }
    } catch (err) {
      console.error('Purchase failed:', err);
      alert('Failed to initiate purchase. Please try again.');
      setPurchasing(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', { 
      day: '2-digit', 
      month: 'short',
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <>
        <PaymentModal paymentModal={paymentModal} setPaymentModal={setPaymentModal} fetchUserData={fetchUserData} />
        <div className="min-h-screen bg-[#0f0f1a] flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Loading your dashboard...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PaymentModal paymentModal={paymentModal} setPaymentModal={setPaymentModal} fetchUserData={fetchUserData} />
      <div className="min-h-screen bg-[#0f0f1a]">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900/50 to-[#1a1a2e] border-b border-purple-500/20">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <span className="text-2xl font-bold">
              <span className="text-white">Site</span>
              <span className="text-purple-400">Orbit</span>
            </span>
            <span className="text-gray-500 text-sm hidden sm:block">| Team Portal</span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-4 py-2 bg-[#252540] hover:bg-[#2a2a4a] text-gray-300 rounded-xl transition-colors"
            data-testid="logout-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400">
            {error}
          </div>
        )}

        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-[#1a1a2e] to-[#252540] rounded-2xl p-6 mb-8 border border-purple-500/20">
          <div className="flex items-center space-x-4">
            {/* Profile Photo */}
            <div className="w-20 h-20 rounded-xl overflow-hidden bg-purple-500/20 flex-shrink-0">
              {user?.profile_photo_url ? (
                <img
                  src={user.profile_photo_url}
                  alt={user.first_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-purple-400 text-2xl font-bold">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white flex items-center">
                Welcome, {user?.first_name}! 
                <span className="ml-2">👋</span>
              </h1>
              <div className="mt-2">
                <span className="inline-block px-3 py-1 bg-purple-500/30 text-purple-300 rounded-full text-sm font-medium">
                  {user?.organisation_name || 'Organisation'}
                </span>
              </div>
              <p className="text-gray-400 mt-1">{user?.role || 'Team Member'}</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Sites Card */}
          <div className="bg-gradient-to-br from-purple-500/20 to-purple-700/10 rounded-2xl p-5 border border-purple-500/20">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-400 text-sm font-medium">Assigned Sites</span>
              <div className="w-10 h-10 bg-purple-500/30 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
            <p className="text-3xl font-bold text-white">{user?.assigned_sites?.length || 0}</p>
            <p className="text-gray-500 text-xs mt-1">Active construction sites</p>
          </div>

          {/* Role Card */}
          <div className="bg-gradient-to-br from-blue-500/20 to-blue-700/10 rounded-2xl p-5 border border-blue-500/20">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-400 text-sm font-medium">Your Role</span>
              <div className="w-10 h-10 bg-blue-500/30 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <p className="text-xl font-bold text-white truncate">{user?.role || 'Team Member'}</p>
            <p className="text-gray-500 text-xs mt-1">{user?.department || 'Department'}</p>
          </div>

          {/* Neo Credits Card - Only show if Neo feature is available */}
          {subscriptionFeatures?.neo && (
            <div className="bg-gradient-to-br from-green-500/20 to-green-700/10 rounded-2xl p-5 border border-green-500/20">
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-400 text-sm font-medium">Neo Credits</span>
                <div className="w-10 h-10 bg-green-500/30 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
              <p className="text-3xl font-bold text-white">{balance?.credits_remaining?.toLocaleString() || 0}</p>
              <p className="text-gray-500 text-xs mt-1">Available credits</p>
            </div>
          )}
        </div>

        {/* User Details Card */}
        <div className="bg-[#1a1a2e] rounded-2xl p-6 mb-8 border border-purple-500/20">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
            <svg className="w-5 h-5 text-purple-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Your Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#252540] rounded-xl p-4">
              <span className="text-gray-500 text-xs uppercase tracking-wider">Full Name</span>
              <p className="text-white mt-1">{user?.first_name} {user?.middle_name} {user?.last_name}</p>
            </div>
            <div className="bg-[#252540] rounded-xl p-4">
              <span className="text-gray-500 text-xs uppercase tracking-wider">Email</span>
              <p className="text-white mt-1">{user?.email || 'Not provided'}</p>
            </div>
            <div className="bg-[#252540] rounded-xl p-4">
              <span className="text-gray-500 text-xs uppercase tracking-wider">Phone</span>
              <p className="text-white mt-1">{user?.phone_number || 'Not provided'}</p>
            </div>
            <div className="bg-[#252540] rounded-xl p-4">
              <span className="text-gray-500 text-xs uppercase tracking-wider">Staff ID</span>
              <p className="text-white mt-1">{user?.staff_id || 'Not assigned'}</p>
            </div>
            <div className="bg-[#252540] rounded-xl p-4">
              <span className="text-gray-500 text-xs uppercase tracking-wider">Organisation ID</span>
              <p className="text-white mt-1 text-sm">{user?.organisation_id || 'N/A'}</p>
            </div>
            <div className="bg-[#252540] rounded-xl p-4">
              <span className="text-gray-500 text-xs uppercase tracking-wider">Assigned Sites</span>
              <p className="text-white mt-1">
                {user?.assigned_site_names?.join(', ') || 'None assigned'}
              </p>
            </div>
          </div>
        </div>

        {/* Neo Credits Purchase Section - Only show if Neo feature is available */}
        {subscriptionFeatures?.neo && (
          <div className="bg-[#1a1a2e] rounded-2xl p-6 border border-purple-500/20">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white flex items-center">
                <svg className="w-5 h-5 text-purple-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Purchase Neo Credits
              </h2>
              <div className="flex items-center space-x-2 bg-purple-500/20 px-4 py-2 rounded-xl">
                <span className="text-gray-400 text-sm">Current Balance:</span>
                <span className="text-purple-400 font-bold">{balance?.credits_remaining?.toLocaleString() || 0}</span>
              </div>
            </div>

            <p className="text-gray-400 text-sm mb-6">
              Neo credits power your AI assistant for tasks, expense help, and more. Purchase additional credits below.
            </p>

            {creditPackages.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {creditPackages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className={`relative bg-[#252540] rounded-xl p-5 border-2 transition-colors ${
                      pkg.popular ? 'border-purple-500 ring-2 ring-purple-500/30' : 'border-purple-500/20 hover:border-purple-500/40'
                    }`}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                          POPULAR
                        </span>
                      </div>
                    )}
                    <div className="text-center mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <h3 className="text-white font-semibold">{pkg.label}</h3>
                    </div>
                    <div className="text-center mb-4">
                      <span className="text-3xl font-bold text-purple-400">{pkg.credits.toLocaleString()}</span>
                      <span className="text-gray-400 ml-1">credits</span>
                    </div>
                    <div className="text-center mb-2">
                      <span className="text-2xl font-bold text-white">£{pkg.price_gbp.toFixed(2)}</span>
                    </div>
                    <div className="text-center mb-4">
                      <span className="text-xs text-gray-400">
                        {(pkg.price_gbp / pkg.credits * 100).toFixed(2)}p per credit
                      </span>
                    </div>
                    <button
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={purchasing}
                      className={`w-full py-3 font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                        pkg.popular
                          ? 'bg-gradient-to-r from-purple-500 to-purple-700 text-white hover:from-purple-600 hover:to-purple-800'
                          : 'bg-[#1a1a2e] text-white border border-purple-500/30 hover:bg-purple-500/20'
                        }`}
                      data-testid={`purchase-btn-${pkg.id}`}
                    >
                      {purchasing ? 'Processing...' : 'Buy Now'}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                </div>
                <p className="text-gray-500">No credit packages available at the moment.</p>
              </div>
            )}
          </div>
        )}

        {/* Purchase History - Collapsible - Only show if Neo feature is available */}
        {subscriptionFeatures?.neo && (
          <div className="mt-8 bg-[#1a1a2e] rounded-2xl border border-purple-500/20 overflow-hidden">
            <button
              onClick={() => setHistoryExpanded(!historyExpanded)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-purple-500/5 transition-colors"
              data-testid="team-purchase-history-toggle"
            >
              <div className="text-left">
                <h2 className="text-lg font-semibold text-white">Purchase History</h2>
                <p className="text-sm text-gray-400 mt-1">Your credit purchase transactions</p>
              </div>
              <svg 
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${historyExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {historyExpanded && (
              <div className="p-6 border-t border-purple-500/10">
                {transactions.filter(t => t.type === 'purchase' && t.status === 'completed').length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full" style={{ tableLayout: 'fixed' }}>
                      <thead>
                        <tr className="bg-[#252540]">
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase" style={{ width: '70%' }}>Transaction Details</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase" style={{ width: '25%' }}>Document</th>
                          <th style={{ width: '5%' }}></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-purple-500/10">
                        {transactions.filter(t => t.type === 'purchase' && t.status === 'completed').map((txn) => (
                          <tr key={txn.transaction_id} className="hover:bg-purple-500/5 transition-colors">
                            <td className="px-4 py-4">
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                <span className="text-sm text-gray-400 whitespace-nowrap">
                                  {formatTimestamp(txn.timestamp)}
                                </span>
                                <span className="text-sm text-white">
                                  {txn.package_label || 'Credit Package'}
                                </span>
                                <span className="text-sm font-semibold text-purple-400">
                                  +{txn.amount?.toLocaleString()} credits
                                </span>
                                <span className="text-sm font-semibold text-white">
                                  £{txn.price_gbp?.toFixed(2)}
                                </span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  txn.status === 'completed' 
                                    ? 'bg-green-500/20 text-green-400' 
                                    : 'bg-amber-500/20 text-amber-400'
                                }`}>
                                  {txn.status}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-center">
                              {(txn.paypal_capture_id || txn.paypal_order_id) ? (
                                <button
                                  onClick={async () => {
                                    if (downloadingInvoice === txn.transaction_id) return;
                                    setDownloadingInvoice(txn.transaction_id);
                                    try {
                                      const response = await fetch(`${API}/neo/credits/invoice/${txn.transaction_id}`, {
                                        headers: { Authorization: `Bearer ${localStorage.getItem('team_token')}` }
                                      });
                                      if (!response.ok) {
                                        const errorData = await response.json();
                                        throw new Error(errorData.detail || 'Failed to download');
                                      }
                                      const blob = await response.blob();
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = `INV-NeoCredits-${txn.transaction_id.slice(-8)}.pdf`;
                                      document.body.appendChild(a);
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      a.remove();
                                    } catch (err) {
                                      setError(err.message || 'Failed to download invoice');
                                    } finally {
                                      setDownloadingInvoice(null);
                                    }
                                  }}
                                  disabled={downloadingInvoice === txn.transaction_id}
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium rounded-lg transition-all duration-200 ${
                                    downloadingInvoice === txn.transaction_id
                                      ? 'opacity-75 cursor-wait'
                                      : 'hover:scale-105 active:scale-95'
                                  }`}
                                  title="Download Invoice"
                                >
                                  {downloadingInvoice === txn.transaction_id ? (
                                    <>
                                      <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      Generating...
                                    </>
                                  ) : (
                                    <>
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      Invoice
                                    </>
                                  )}
                                </button>
                              ) : (
                                <span className="text-xs text-gray-500">-</span>
                              )}
                            </td>
                            <td></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <p className="text-sm">No purchase history yet</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Mobile App Notice */}
        <div className="mt-8 bg-gradient-to-r from-purple-900/30 to-[#1a1a2e] rounded-2xl p-6 border border-purple-500/20">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-1">Download the Mobile App</h3>
              <p className="text-gray-400 text-sm">
                For full features including clock in/out, photo uploads, task management, and more - download the SiteOrbit mobile app from your app store.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-purple-500/10 mt-8">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-gray-600 text-sm">
          © {new Date().getFullYear()} SiteOrbit. All rights reserved.
        </div>
      </footer>
      </div>
    </>
  );
};

export default TeamDashboardPage;
